import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { Grid, TextField, Button, Select, MenuItem, InputLabel, FormControl, FormHelperText, Alert } from '@mui/material';
import FormPage from '../components/FormPage';
import { createSalesNewInstallation, fetchCustomers, fetchSolutions } from '../services/ApiService';

export function AddSale() {
  const navigate = useNavigate();
  const [haulierOptions, setHaulierOptions] = React.useState({});
  const [devicesOptions, setDevicesOptions] = React.useState({});
  const [document, setDocument] = React.useState(null);
  const [formData, setFormData] = React.useState({
    customer: '', corporate: '', poc_name: '', poc_contact: '', solution: '', features: '', plan: '', unit: '',
  });
  const [formErrors, setFormErrors] = React.useState({});

  React.useEffect(() => {
    fetchCustomers().then((res) => {
      setHaulierOptions(res.data.reduce((acc, c) => ({ ...acc, [c.id]: c.name }), {}));
    }).catch(() => {});
    fetchSolutions().then((res) => {
      setDevicesOptions(res.data.reduce((acc, s) => ({ ...acc, [s.id]: s.name }), {}));
    }).catch(() => {});
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: null }));
  };

  const handleDocumentChange = (e) => setDocument(e.target.files[0]);

  const handleCancel = () => navigate('/sales-new-installation');

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = new FormData();
      Object.entries(formData).forEach(([key, value]) => payload.append(key, value));
      if (document) payload.append('document', document);

      await createSalesNewInstallation(payload);
      navigate('/sales-new-installation', { state: { successMessage: 'Sale created successfully' } });
    } catch (error) {
      if (error.response?.data) setFormErrors(error.response.data);
      else setFormErrors({ general: error.message });
    }
  };

  return (
    <FormPage
      title="Add New Sales Entry"
      backTo="/sales-new-installation"
      onSubmit={handleSubmit}
      actions={<>
        <Button variant="outlined" onClick={handleCancel}>Cancel</Button>
        <Button variant="contained" type="submit">Save</Button>
      </>}
    >
      {formErrors.general && (
        <Alert severity="error" sx={{ mb: 2 }}>{formErrors.general}</Alert>
      )}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth required error={!!formErrors.customer}>
            <InputLabel>Customer</InputLabel>
            <Select name="customer" value={formData.customer} onChange={handleChange} label="Customer" displayEmpty>
              <MenuItem value="" disabled>Select a customer</MenuItem>
              {Object.entries(haulierOptions).map(([key, value]) => (
                <MenuItem key={key} value={key}>{value}</MenuItem>
              ))}
            </Select>
            {formErrors.customer && <FormHelperText>{formErrors.customer}</FormHelperText>}
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth required error={!!formErrors.solution}>
            <InputLabel>Solution</InputLabel>
            <Select name="solution" value={formData.solution} onChange={handleChange} label="Solution" displayEmpty>
              <MenuItem value="" disabled>Select a solution</MenuItem>
              {Object.entries(devicesOptions).map(([key, value]) => (
                <MenuItem key={key} value={key}>{value}</MenuItem>
              ))}
            </Select>
            {formErrors.solution && <FormHelperText>{formErrors.solution}</FormHelperText>}
          </FormControl>
        </Grid>

        {[
          { name: 'corporate', label: 'Corporate', placeholder: 'e.g. Wafi / UEP / TPPL' },
          { name: 'poc_name', label: 'POC Name', placeholder: 'Point of contact name' },
          { name: 'poc_contact', label: 'POC Contact', placeholder: 'Phone number or email' },
          { name: 'unit', label: 'Unit', placeholder: 'e.g. Unit 1' },
        ].map((field) => (
          <Grid item xs={12} sm={6} key={field.name}>
            <TextField
              fullWidth required label={field.label} name={field.name} placeholder={field.placeholder}
              value={formData[field.name]} onChange={handleChange}
              error={!!formErrors[field.name]} helperText={formErrors[field.name]?.[0]}
            />
          </Grid>
        ))}

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth required error={!!formErrors.plan}>
            <InputLabel>Plan</InputLabel>
            <Select name="plan" value={formData.plan} onChange={handleChange} label="Plan" displayEmpty>
              <MenuItem value="" disabled>Select a plan</MenuItem>
              <MenuItem value="New Install">New Install</MenuItem>
              <MenuItem value="Demo">Demo</MenuItem>
            </Select>
            {formErrors.plan && <FormHelperText>{formErrors.plan[0]}</FormHelperText>}
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth multiline rows={4} label="Features" name="features"
            placeholder="Describe the features included in this sale"
            value={formData.features} onChange={handleChange}
            error={!!formErrors.features} helperText={formErrors.features?.[0]}
          />
        </Grid>

        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel shrink>Upload Document</InputLabel>
            <input
              type="file"
              accept=".pdf,.doc,.docx,.txt,image/*"
              name="document"
              onChange={handleDocumentChange}
              style={{ marginTop: 8 }}
            />
            <FormHelperText>Accepted formats: PDF, DOC, DOCX, TXT, or image files</FormHelperText>
          </FormControl>
        </Grid>
      </Grid>
    </FormPage>
  );
}
